import './chartPieWithList.less'
import LoaderView from '../../components/Loader/loader'
import ChartPie, { ChartPieItem } from '../../components/ChartPie/chartPie'
import NoSensitiveDataSvg from '../../assets/inlineSvg/noData.svg'
import { Link } from 'react-router-dom'
import cx from 'classnames'
import React, { SVGProps } from 'react'
import { useIntl } from 'react-intl'
import { Tooltip, Typography } from '@lightbeamai/design-system'
import { SemanticSIZES } from 'semantic-ui-react/dist/commonjs/generic'

const LinkWrapper = (props: {
  link: string
  className?: string
  target: string
  children: React.ReactElement | React.ReactElement[]
  isCapitalize?: boolean
}) => {
  const { children, className = '', link = '', target, isCapitalize = true } = props

  return link ? (
    <Link
      to={link}
      className={cx(className, { 'link-text-capitalize': isCapitalize })}
      target={target}
    >
      {children}
    </Link>
  ) : (
    <div className={className}>{children}</div>
  )
}

type ColumnHeadings = {
  heading1: string
  heading2: string
}

interface IProps {
  title?: string
  data: ChartPieItem[]
  showLoader?: boolean
  loaderSize?: SemanticSIZES
  text?: string
  pieLink?: Array<{ key: string; link: string }> | string
  total?: number
  totalLink?: string
  compact?: boolean
  newWindow?: boolean
  showHalfCircle?: boolean
  onClick?: (val: string) => void
  showPercentage?: boolean
  emptyState?: boolean | React.ReactElement
  horizontal?: boolean
  listTitle?: string | React.ReactElement
  denseList?: boolean
  width?: number
  centerTextStyles?: React.CSSProperties & SVGProps<SVGAElement>
  centerCountStyles?: React.CSSProperties & SVGProps<SVGAElement>
  customToolTip?: React.ReactElement
  listTitleColumnHeadings?: ColumnHeadings
  isCapitalize?: boolean
}

const ChartPieWithList = (props: IProps): React.ReactElement => {
  const {
    data,
    title,
    text,
    showLoader = false,
    loaderSize = 'medium',
    pieLink,
    total,
    compact = true,
    newWindow = false,
    totalLink,
    showHalfCircle = false,
    onClick,
    showPercentage = false,
    emptyState = true,
    horizontal = false,
    listTitle,
    denseList = false,
    width = 150,
    centerTextStyles = {},
    centerCountStyles = {},
    customToolTip,
    listTitleColumnHeadings,
    isCapitalize = true
  } = props
  const intl = useIntl()

  if (!data.length && emptyState) {
    return (
      <div className="widget-empty-state">
        <div className="empty-icon-box">
          <NoSensitiveDataSvg />
        </div>
        <div className="text">{intl.formatMessage({ id: 'noDataFound' })}</div>
        <LoaderView size={loaderSize} showText={false} showLoader={showLoader} />
      </div>
    )
  }
  const PieWithList = () => (
    <>
      <ChartPie
        data={data}
        text={text}
        newWindow={newWindow}
        showHalfCircle={showHalfCircle}
        total={total || undefined}
        {...(onClick ? { onClick } : '')}
        {...(totalLink ? { totalLink } : {})}
        {...(pieLink ? { link: pieLink } : {})}
        width={width}
        centerTextStyles={centerTextStyles}
        centerCountStyles={centerCountStyles}
        customToolTip={customToolTip}
      />
      <div className={cx('list-with-title', { 'dense-list': denseList })}>
        {!compact && title && <div className="title">{title}</div>}
        <div className="list">
          {listTitle ? listTitle : <></>}
          {listTitleColumnHeadings && (
            <div className="chartpie-list-title">
              <Typography as="div" variant="baseMinus4" type="strong">
                {listTitleColumnHeadings.heading1}
              </Typography>
              <Typography as="div" variant="baseMinus4" type="strong">
                {listTitleColumnHeadings.heading2}
              </Typography>
            </div>
          )}
          {data.map((a, i) => (
            <LinkWrapper
              link={a.link ? a.link : ''}
              className={`item ${a.link ? 'btn' : ''}`}
              isCapitalize={isCapitalize}
              target={newWindow ? '_blank' : '_self'}
              key={i}
            >
              <div className="color-box" style={{ backgroundColor: a.color }} />
              <Tooltip title={a.key} placement="top">
                <Typography as="div" color="secondary" variant={'baseMinus4'} className="name">
                  <Typography className="name-content">{a.key}</Typography>
                </Typography>
              </Tooltip>
              {showPercentage ? (
                <div className="percentage">
                  {a.percentage ? (
                    <>
                      {intl.formatNumber(a.percentage, {
                        style: 'decimal',
                        maximumFractionDigits: 2
                      })}
                      %
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              ) : (
                <></>
              )}
              <div className="count">
                {a.value ? <>{intl.formatNumber(a.value, { notation: 'compact' })}</> : '-'}
              </div>
            </LinkWrapper>
          ))}
        </div>
      </div>
    </>
  )
  return (
    <div className={`${cx('chart-pie-with-list', { compact })}`} data-test-id="chart-pie-with-list">
      {compact && title && (
        <Typography as="div" color="secondary" variant="baseMinus2" type="medium" className="title">
          {title}
        </Typography>
      )}
      {horizontal ? (
        <div className={'horizontal widget-content'}>
          <PieWithList />
        </div>
      ) : (
        <PieWithList />
      )}
      <LoaderView showText={false} showLoader={showLoader} />
    </div>
  )
}

export default ChartPieWithList
